import type { ConversationTranscript } from '@/entities/conversations';
import { PHRASE_TYPES } from '@/entities/conversations';
import { scrollIntoViewWithinContainer } from '@/shared/lib';
import { getScrollAreaViewport } from './getScrollAreaViewport';
import { highlightCloud } from './highlightCloud';

/**
 * Resolve the group leader phrase id for a given phrase id.
 * Transcript UI renders message groups; the DOM node uses the first phrase id in the group (data-message-id).
 */
export const getGroupLeaderId = (transcript: ConversationTranscript[], phraseId: number): number => {
  const spokenPhrases = transcript.filter((p) => p.type === PHRASE_TYPES.PHRASE && p.person != null);
  const phrase = spokenPhrases.find((p) => p.id === phraseId);
  if (!phrase) return phraseId;

  const indexInFull = transcript.indexOf(phrase);
  let groupLeaderId = phrase.id;

  if (indexInFull > 0) {
    const speakerId = phrase.person!.id;
    for (let i = indexInFull - 1; i >= 0; i--) {
      const prev = transcript[i];
      if (prev.type === PHRASE_TYPES.PAUSE || prev.type === PHRASE_TYPES.INTERRUPTION) {
        break;
      }
      if (prev.person == null || prev.person.id !== speakerId) {
        break;
      }
      if (prev.type === PHRASE_TYPES.PHRASE) {
        groupLeaderId = prev.id;
      }
    }
  }

  return groupLeaderId;
};

/**
 * Scroll transcript view to the message with the given fragment id and highlight it.
 * Uses data-message-id in the DOM (group leader id for message groups).
 * When container is provided, search and scroll are limited to that subtree.
 *
 * @param fragmentId - Phrase/fragment id (string)
 * @param transcripts - Transcript data to resolve group leader id
 * @param container - Optional root element of the transcript; when set, only this subtree is used
 */
export const scrollToTranscriptFragment = (
  fragmentId: string,
  transcripts: ConversationTranscript[],
  container?: HTMLElement | null,
): void => {
  const phraseId = Number(fragmentId);
  if (Number.isNaN(phraseId) || transcripts.length === 0) return;

  const groupLeaderId = getGroupLeaderId(transcripts, phraseId);
  const phraseElement = container
    ? container.querySelector<HTMLElement>(`[data-message-id="${groupLeaderId}"]`)
    : document.querySelector<HTMLElement>(`[data-message-id="${groupLeaderId}"]`);

  if (phraseElement) {
    scrollIntoViewWithinContainer(phraseElement, {
      align: 'center',
      behavior: 'smooth',
      container: getScrollAreaViewport(phraseElement),
    });

    highlightCloud(phraseElement);
  }
};
